'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Copy,
  Link2,
  Loader2,
  Mail,
  MessageCircle,
  Send,
  Smartphone,
  Users,
  X,
} from 'lucide-react';
import EmailRecipientsInput from '@/app/client-zone/modules/total-connect/components/EmailRecipientsInput';
import EmailContactPickerModal from '@/components/reports/EmailContactPickerModal';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { Proposal, SendProposalToClientRequest } from '@/lib/proposals/proposalTypes';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('ProposalSendDialog');

type ProposalSendMethod = SendProposalToClientRequest['send_method'];
type EmailField = 'to' | 'cc' | 'bcc';
type EmailSuggestionSource = 'recent' | 'contact' | 'company' | 'crm' | 'personal';

interface ProposalSendDelivery {
  channel: ProposalSendMethod;
  shareUrl?: string;
  shareToken?: string;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
}

interface ProposalSendDialogProps {
  isOpen: boolean;
  proposal: Proposal | null;
  onClose: () => void;
  onSent?: (proposal: Proposal, delivery?: ProposalSendDelivery) => void;
  allowedSendMethods?: ProposalSendMethod[];
  initialSendMethod?: ProposalSendMethod;
}

interface EmailSuggestion {
  email: string;
  name?: string;
  source: EmailSuggestionSource;
  avatar_url?: string;
  photo_url?: string;
  job_title?: string;
  contact_type?: string;
  company?: string;
}

type ProposalSendActionPayload = SendProposalToClientRequest & {
  action: 'send_to_client';
};

const SEND_METHOD_OPTIONS: Array<{
  method: ProposalSendMethod;
  label: string;
  description: string;
  icon: typeof Mail;
}> = [
  {
    method: 'email',
    label: 'Email PDF',
    description: 'Send a branded PDF attachment by email',
    icon: Mail,
  },
  {
    method: 'sms',
    label: 'SMS Link',
    description: 'Send the proposal permalink by SMS',
    icon: Smartphone,
  },
  {
    method: 'whatsapp',
    label: 'WhatsApp Link',
    description: 'Send the proposal permalink by WhatsApp',
    icon: MessageCircle,
  },
  {
    method: 'share_link',
    label: 'Manual Share Link',
    description: 'Generate a shareable link and copy it manually',
    icon: Link2,
  },
] as const;

const EMAIL_SUGGESTION_SOURCES: readonly EmailSuggestionSource[] = ['recent', 'contact', 'company', 'crm', 'personal'] as const;

function isEmailSuggestionSource(value: unknown): value is EmailSuggestionSource {
  return typeof value === 'string' && EMAIL_SUGGESTION_SOURCES.includes(value as EmailSuggestionSource);
}

function normalizeEmailSuggestion(suggestion: Partial<EmailSuggestion>): EmailSuggestion | null {
  const email = typeof suggestion.email === 'string' ? suggestion.email.trim() : '';
  if (!email) {
    return null;
  }

  return {
    email,
    name: typeof suggestion.name === 'string' ? suggestion.name : undefined,
    source: isEmailSuggestionSource(suggestion.source) ? suggestion.source : 'recent',
    avatar_url: typeof suggestion.avatar_url === 'string' ? suggestion.avatar_url : undefined,
    photo_url: typeof suggestion.photo_url === 'string' ? suggestion.photo_url : undefined,
    job_title: typeof suggestion.job_title === 'string' ? suggestion.job_title : undefined,
    contact_type: typeof suggestion.contact_type === 'string' ? suggestion.contact_type : undefined,
    company: typeof suggestion.company === 'string' ? suggestion.company : undefined,
  };
}

function dedupeEmailSuggestions(suggestions: EmailSuggestion[]): EmailSuggestion[] {
  return suggestions.filter(
    (suggestion, index, arr) => arr.findIndex((item) => item.email.toLowerCase() === suggestion.email.toLowerCase()) === index,
  );
}

function extractEmailAddresses(value: string): string[] {
  if (!value?.trim()) return [];

  const parts: string[] = [];
  let current = '';
  let inBracket = false;

  for (const char of value) {
    if (char === '<') inBracket = true;
    if (char === '>') inBracket = false;

    if (char === ',' && !inBracket) {
      if (current.trim()) parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim()) parts.push(current.trim());

  return parts
    .map((part) => {
      const match = part.match(/<([^<>]+@[^<>]+)>/);
      return match ? match[1].trim() : part.trim();
    })
    .filter((email) => email.includes('@'));
}

function formatProposalAmount(proposal: Proposal) {
  try {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: proposal.currency || 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(proposal.grand_total || 0));
  } catch {
    return `${proposal.currency || 'ZAR'} ${Number(proposal.grand_total || 0).toFixed(2)}`;
  }
}

function formatDateLabel(value?: string | null) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}

export default function ProposalSendDialog({
  isOpen,
  proposal,
  onClose,
  onSent,
  allowedSendMethods,
  initialSendMethod,
}: ProposalSendDialogProps) {
  const [sendMethod, setSendMethod] = useState<ProposalSendMethod>('email');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailName, setEmailName] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState<EmailSuggestion[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [selectedEmailSuggestionIndex, setSelectedEmailSuggestionIndex] = useState(-1);
  const [activeEmailField, setActiveEmailField] = useState<EmailField>('to');
  const emailToInputRef = useRef<HTMLInputElement>(null);
  const emailCcInputRef = useRef<HTMLInputElement>(null);
  const emailBccInputRef = useRef<HTMLInputElement>(null);

  const enabledSendMethodOptions = useMemo(
    () => SEND_METHOD_OPTIONS.filter((option) => !allowedSendMethods || allowedSendMethods.includes(option.method)),
    [allowedSendMethods],
  );

  const activeMethod = useMemo(
    () => enabledSendMethodOptions.find((option) => option.method === sendMethod) || enabledSendMethodOptions[0] || null,
    [enabledSendMethodOptions, sendMethod],
  );

  const proposalAmount = useMemo(() => (proposal ? formatProposalAmount(proposal) : null), [proposal]);
  const validUntilLabel = useMemo(() => formatDateLabel(proposal?.valid_until), [proposal?.valid_until]);
  const emailRecipients = useMemo(() => extractEmailAddresses(emailTo), [emailTo]);
  const emailCcRecipients = useMemo(() => extractEmailAddresses(emailCc), [emailCc]);
  const emailBccRecipients = useMemo(() => extractEmailAddresses(emailBcc), [emailBcc]);
  const hasEmailRecipient = emailRecipients.length > 0;
  const hasPhoneRecipient = recipientPhone.trim().length > 0;
  const isEmailReady = sendMethod !== 'email' || hasEmailRecipient;
  const isPhoneReady = (sendMethod !== 'sms' && sendMethod !== 'whatsapp') || hasPhoneRecipient;
  const canSend = Boolean(proposal?.id) && enabledSendMethodOptions.length > 0 && isEmailReady && isPhoneReady && !isSending;

  const appendRecipientsToField = useCallback((recipients: Array<{ email: string; name?: string }>, field: EmailField = 'to') => {
    const currentValue = field === 'cc' ? emailCc : field === 'bcc' ? emailBcc : emailTo;
    const currentEmails = new Set(extractEmailAddresses(currentValue).map((email) => email.toLowerCase()));
    const existingParts = currentValue.split(',').map((part) => part.trim()).filter(Boolean);
    const additions = recipients
      .filter((recipient) => !currentEmails.has(recipient.email.toLowerCase()))
      .map((recipient) => recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email);
    const nextValue = [...existingParts, ...additions].join(', ');

    if (field === 'cc') {
      setEmailCc(nextValue ? `${nextValue}, ` : '');
    } else if (field === 'bcc') {
      setEmailBcc(nextValue ? `${nextValue}, ` : '');
    } else {
      setEmailTo(nextValue ? `${nextValue}, ` : '');
    }
  }, [emailBcc, emailCc, emailTo]);

  const loadEmailSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }

    try {
      const q = query.toLowerCase();
      const recent = JSON.parse(localStorage.getItem('recentEmailRecipients') || '[]') as Partial<EmailSuggestion>[];
      const company = JSON.parse(localStorage.getItem('companyEmailUsers') || '[]') as Partial<EmailSuggestion>[];
      const cached = dedupeEmailSuggestions(
        [...recent, ...company]
          .map(normalizeEmailSuggestion)
          .filter((suggestion): suggestion is EmailSuggestion => Boolean(suggestion))
          .filter((suggestion) => suggestion.email.toLowerCase().includes(q) || suggestion.name?.toLowerCase().includes(q)),
      );
      setEmailSuggestions(cached.slice(0, 8));
      setShowEmailSuggestions(cached.length > 0);
      setSelectedEmailSuggestionIndex(-1);

      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'searchAllContacts', query }),
      });

      if (response.ok) {
        const data = await response.json() as { success?: boolean; data?: Partial<EmailSuggestion>[] };
        const remoteSuggestions = Array.isArray(data.data) ? data.data : [];
        if (data.success && remoteSuggestions.length > 0) {
          const deduped = dedupeEmailSuggestions([
            ...cached,
            ...remoteSuggestions
              .map((contact) => normalizeEmailSuggestion({
                email: contact.email,
                name: contact.name,
                source: contact.source,
                avatar_url: contact.avatar_url || contact.photo_url,
                job_title: contact.job_title,
                contact_type: contact.contact_type,
                company: contact.company,
              }))
              .filter((suggestion): suggestion is EmailSuggestion => Boolean(suggestion)),
          ]);
          setEmailSuggestions(deduped.slice(0, 10));
          setShowEmailSuggestions(deduped.length > 0);
        }
      }
    } catch {
      // cached suggestions are enough of a fallback
    }
  }, []);

  const selectEmailSuggestion = useCallback((suggestion: EmailSuggestion) => {
    const current = activeEmailField === 'cc' ? emailCc : activeEmailField === 'bcc' ? emailBcc : emailTo;
    const setter = activeEmailField === 'cc' ? setEmailCc : activeEmailField === 'bcc' ? setEmailBcc : setEmailTo;
    const parts = current.split(',').map((part) => part.trim());
    parts[parts.length - 1] = suggestion.name ? `${suggestion.name} <${suggestion.email}>` : suggestion.email;
    setter(parts.join(', ') + ', ');
    setShowEmailSuggestions(false);
    setEmailSuggestions([]);
    setSelectedEmailSuggestionIndex(-1);
  }, [activeEmailField, emailBcc, emailCc, emailTo]);

  const handleEmailFieldKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!showEmailSuggestions || emailSuggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedEmailSuggestionIndex((index) => Math.min(index + 1, emailSuggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedEmailSuggestionIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && selectedEmailSuggestionIndex >= 0) {
      event.preventDefault();
      selectEmailSuggestion(emailSuggestions[selectedEmailSuggestionIndex]);
    } else if (event.key === 'Escape') {
      setShowEmailSuggestions(false);
    }
  }, [emailSuggestions, selectedEmailSuggestionIndex, selectEmailSuggestion, showEmailSuggestions]);

  useEffect(() => {
    if (!isOpen || !proposal) {
      return;
    }

    const nextMethod: ProposalSendMethod = proposal.contact_email
      ? 'email'
      : proposal.contact_phone
        ? 'whatsapp'
        : 'share_link';

    const fallbackMethod = enabledSendMethodOptions[0]?.method;
    const preferredMethod = initialSendMethod && enabledSendMethodOptions.some((option) => option.method === initialSendMethod)
      ? initialSendMethod
      : null;
    const resolvedMethod = preferredMethod
      || (enabledSendMethodOptions.some((option) => option.method === nextMethod)
        ? nextMethod
        : fallbackMethod || nextMethod);

    setSendMethod(resolvedMethod);
    setEmailTo(
      proposal.contact_email
        ? proposal.contact_name || proposal.contact_person_name
          ? `${proposal.contact_name || proposal.contact_person_name} <${proposal.contact_email}>, `
          : `${proposal.contact_email}, `
        : '',
    );
    setEmailCc('');
    setEmailBcc('');
    setEmailSubject(
      proposal.proposal_number
        ? `Proposal ${proposal.proposal_number}`
        : proposal.title?.trim()
          ? proposal.title.trim()
          : 'Proposal',
    );
    setEmailName(proposal.contact_name || proposal.contact_person_name || '');
    setRecipientPhone(proposal.contact_phone || '');
    setMessage('');
    setIsSending(false);
    setCopied(false);
    setShareUrl(null);
    setErrors({});
    setShowCcBcc(false);
    setContactPickerOpen(false);
    setEmailSuggestions([]);
    setShowEmailSuggestions(false);
    setSelectedEmailSuggestionIndex(-1);
    setActiveEmailField('to');
  }, [enabledSendMethodOptions, initialSendMethod, isOpen, proposal]);

  const validate = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!proposal?.id) {
      nextErrors.general = 'Proposal is missing or not yet saved';
    }

    if (sendMethod === 'email' && emailRecipients.length === 0) {
      nextErrors.recipient_email = 'Enter at least one recipient email address';
    }

    if ((sendMethod === 'sms' || sendMethod === 'whatsapp') && !recipientPhone.trim()) {
      nextErrors.recipient_phone = 'Enter a phone number';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [emailRecipients.length, proposal?.id, recipientPhone, sendMethod]);

  const handleClose = useCallback(() => {
    if (isSending) {
      return;
    }

    onClose();
  }, [isSending, onClose]);

  const handleCopyShareUrl = useCallback(async () => {
    if (!shareUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      notify.success('Share link copied to clipboard');
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      logger.error('[ProposalSendDialog] Failed to copy share link:', error);
      notify.error('Failed to copy share link');
    }
  }, [shareUrl]);

  const handleSend = useCallback(async () => {
    if (!proposal || !validate()) {
      return;
    }

    if (!enabledSendMethodOptions.some((option) => option.method === sendMethod)) {
      notify.error('No client-approved delivery channels are available for this proposal');
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      notify.error('Authentication required');
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch('/api/v1/proposals', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'send_to_client',
          proposal_id: proposal.id,
          send_method: sendMethod,
          recipient_email: sendMethod === 'email' ? emailRecipients[0] : undefined,
          recipient_emails: sendMethod === 'email' && emailRecipients.length > 0 ? emailRecipients : undefined,
          recipient_phone: sendMethod === 'sms' || sendMethod === 'whatsapp' ? recipientPhone.trim() : undefined,
          recipient_name: sendMethod === 'email'
            ? (emailName.trim() || proposal.contact_name || proposal.contact_person_name || undefined)
            : undefined,
          cc: sendMethod === 'email' && emailCcRecipients.length > 0 ? emailCcRecipients : undefined,
          bcc: sendMethod === 'email' && emailBccRecipients.length > 0 ? emailBccRecipients : undefined,
          subject: sendMethod === 'email' ? (emailSubject.trim() || undefined) : undefined,
          message: message.trim() || undefined,
        } satisfies ProposalSendActionPayload),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || `Failed to send proposal via ${sendMethod}`);
      }

      const updatedProposal = payload.data as Proposal;
      const delivery = payload.delivery as ProposalSendDelivery | undefined;

      if (sendMethod === 'share_link' && !delivery?.shareUrl) {
        throw new Error('Share link was generated without a usable URL');
      }

      onSent?.(updatedProposal, delivery);

      if (sendMethod === 'share_link' && delivery?.shareUrl) {
        setShareUrl(delivery.shareUrl);
        notify.success('Share link generated');
      } else {
        notify.success(
          sendMethod === 'email'
            ? 'Proposal emailed successfully'
            : sendMethod === 'sms'
              ? 'Proposal link sent via SMS'
              : 'Proposal link sent via WhatsApp',
        );
        onClose();
      }
    } catch (error) {
      logger.error('[ProposalSendDialog] Error sending proposal:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to send proposal');
    } finally {
      setIsSending(false);
    }
  }, [emailBccRecipients, emailCcRecipients, emailName, emailRecipients, emailSubject, enabledSendMethodOptions, message, onClose, onSent, proposal, recipientPhone, sendMethod, validate]);

  if (!isOpen || !proposal) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send Proposal</h2>
            <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-400">
              {proposal.title}
              {proposal.proposal_number ? ` · #${proposal.proposal_number}` : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Contact</p>
              <p className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {proposal.contact_name || proposal.contact_person_name || 'Not set yet'}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</p>
              <p className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{proposalAmount}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Valid until</p>
              <p className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{validUntilLabel || 'Not specified'}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {enabledSendMethodOptions.map((option) => {
              const Icon = option.icon;
              const selected = sendMethod === option.method;

              return (
                <button
                  key={option.method}
                  type="button"
                  onClick={() => {
                    setSendMethod(option.method);
                    setErrors((current) => {
                      const next = { ...current };
                      delete next.recipient_email;
                      delete next.recipient_phone;
                      return next;
                    });
                  }}
                  className={`rounded-xl border p-3 text-left transition-colors ${selected
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                    : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/70'
                    }`}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{option.description}</p>
                </button>
              );
            })}
          </div>

          {!shareUrl && enabledSendMethodOptions.length === 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
              No delivery channels are currently enabled for AI proposals in Industry Solution Settings. Enable email, SMS, WhatsApp, or share-link delivery to send this proposal to a client.
            </div>
          )}

          {!shareUrl && enabledSendMethodOptions.length > 0 && (
            <>
              {sendMethod === 'email' && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
                    <div className="flex items-start gap-2">
                      <label className="w-8 shrink-0 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">To</label>
                      <div className="flex-1">
                        <EmailRecipientsInput
                          value={emailTo}
                          onChange={setEmailTo}
                          onFocus={() => setActiveEmailField('to')}
                          suggestions={emailSuggestions}
                          showSuggestions={showEmailSuggestions && activeEmailField === 'to'}
                          selectedSuggestionIndex={selectedEmailSuggestionIndex}
                          onLoadSuggestions={loadEmailSuggestions}
                          onSelectSuggestion={selectEmailSuggestion}
                          onKeyDown={handleEmailFieldKeyDown}
                          inputRef={emailToInputRef}
                          placeholder="client@example.com"
                          className="min-h-[34px] rounded-lg bg-white px-2 py-1.5 dark:bg-gray-900"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setContactPickerOpen(true)}
                        className={`shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${emailTo.trim()
                          ? 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                      >
                        <Users className="h-3.5 w-3.5" />
                        {emailTo.trim() ? 'CRM list' : 'Pick from CRM'}
                      </button>
                    </div>
                    {errors.recipient_email && <p className="mt-1 ml-10 text-xs text-red-500">{errors.recipient_email}</p>}
                    {!hasEmailRecipient && !errors.recipient_email && (
                      <p className="mt-1 ml-10 text-[11px] text-blue-600 dark:text-blue-400">
                        No recipient yet? Open the CRM list and add one in a click.
                      </p>
                    )}
                  </div>

                  <input
                    type="text"
                    placeholder="Recipient name (optional)"
                    value={emailName}
                    onChange={(event) => setEmailName(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />

                  <div className="flex items-center justify-between">
                    {!showCcBcc ? (
                      <button
                        type="button"
                        onClick={() => setShowCcBcc(true)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                      >
                        Cc / Bcc
                      </button>
                    ) : <span />}
                  </div>

                  {showCcBcc && (
                    <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60">
                      <div className="flex items-start gap-2">
                        <label className="w-8 shrink-0 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">Cc</label>
                        <div className="flex-1">
                          <EmailRecipientsInput
                            value={emailCc}
                            onChange={setEmailCc}
                            onFocus={() => setActiveEmailField('cc')}
                            suggestions={emailSuggestions}
                            showSuggestions={showEmailSuggestions && activeEmailField === 'cc'}
                            selectedSuggestionIndex={selectedEmailSuggestionIndex}
                            onLoadSuggestions={loadEmailSuggestions}
                            onSelectSuggestion={selectEmailSuggestion}
                            onKeyDown={handleEmailFieldKeyDown}
                            inputRef={emailCcInputRef}
                            placeholder="Optional Cc recipients"
                            className="min-h-[34px] rounded-lg bg-white px-2 py-1.5 dark:bg-gray-900"
                          />
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <label className="w-8 shrink-0 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">Bcc</label>
                        <div className="flex-1">
                          <EmailRecipientsInput
                            value={emailBcc}
                            onChange={setEmailBcc}
                            onFocus={() => setActiveEmailField('bcc')}
                            suggestions={emailSuggestions}
                            showSuggestions={showEmailSuggestions && activeEmailField === 'bcc'}
                            selectedSuggestionIndex={selectedEmailSuggestionIndex}
                            onLoadSuggestions={loadEmailSuggestions}
                            onSelectSuggestion={selectEmailSuggestion}
                            onKeyDown={handleEmailFieldKeyDown}
                            inputRef={emailBccInputRef}
                            placeholder="Optional Bcc recipients"
                            className="min-h-[34px] rounded-lg bg-white px-2 py-1.5 dark:bg-gray-900"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-2">
                    <label className="w-10 shrink-0 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">Subj</label>
                    <input
                      type="text"
                      placeholder="Subject line"
                      value={emailSubject}
                      onChange={(event) => setEmailSubject(event.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </div>
                </div>
              )}

              {(sendMethod === 'sms' || sendMethod === 'whatsapp') && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Recipient phone</label>
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(event) => setRecipientPhone(event.target.value)}
                    placeholder="+27 82 123 4567"
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.recipient_phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100`}
                  />
                  {errors.recipient_phone && <p className="mt-1 text-xs text-red-500">{errors.recipient_phone}</p>}
                  {!hasPhoneRecipient && !errors.recipient_phone && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      No contact phone is stored on this proposal yet — enter one here or generate a manual share link instead.
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={sendMethod === 'share_link'
                    ? 'Optional note for whoever you share the link with...'
                    : `Add a personal message for this ${(activeMethod?.label || 'delivery option').toLowerCase()}...`
                  }
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400">
                {sendMethod === 'email' && 'A branded proposal PDF will be generated and emailed to the selected recipients.'}
                {sendMethod === 'sms' && 'The recipient will receive the proposal work-item link by SMS.'}
                {sendMethod === 'whatsapp' && 'The recipient will receive the proposal work-item link by WhatsApp.'}
                {sendMethod === 'share_link' && 'A shareable proposal link will be generated so you can copy and send it manually.'}
              </div>

              {sendMethod === 'email' && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400">
                  {hasEmailRecipient
                    ? `${emailRecipients.length} recipient${emailRecipients.length === 1 ? '' : 's'}${emailCcRecipients.length > 0 ? ` + ${emailCcRecipients.length} Cc` : ''}${emailBccRecipients.length > 0 ? ` + ${emailBccRecipients.length} Bcc` : ''}`
                    : 'No recipients yet'}
                </div>
              )}

              {sendMethod === 'email' && hasEmailRecipient && !errors.recipient_email && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The PDF will be sent to <span className="font-medium text-gray-700 dark:text-gray-200">{emailRecipients.join(', ')}</span>.
                </p>
              )}

              {(sendMethod === 'sms' || sendMethod === 'whatsapp') && hasPhoneRecipient && !errors.recipient_phone && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The link will be delivered to <span className="font-medium text-gray-700 dark:text-gray-200">{recipientPhone.trim()}</span>.
                </p>
              )}
            </>
          )}

          {shareUrl && (
            <div className="space-y-4">
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-4 dark:border-green-900/40 dark:bg-green-950/30">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-green-600 p-2 text-white">
                    <Check className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100">Share link ready</p>
                    <p className="mt-1 text-sm text-green-800 dark:text-green-200">
                      The proposal is now awaiting client approval. Copy the link below and share it with the client.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Proposal link</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => void handleCopyShareUrl()}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div className="mt-2">
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Open the proposal review page
                  </a>
                </div>
              </div>
            </div>
          )}

          {errors.general && (
            <p className="text-sm text-red-500">{errors.general}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {shareUrl ? 'Done' : 'Cancel'}
          </button>
          {!shareUrl && (
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isSending ? 'Sending…' : sendMethod === 'share_link' ? 'Generate Link' : 'Send Proposal'}
            </button>
          )}
        </div>
      </div>

      <EmailContactPickerModal
        isOpen={contactPickerOpen}
        onClose={() => setContactPickerOpen(false)}
        onAddRecipients={(recipients) => appendRecipientsToField(recipients, 'to')}
        title={`Add ${proposal.title || 'proposal'} recipients`}
        defaultTab="crm"
        allowedTabs={['crm']}
      />
    </div>
  );
}
